import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ShopifyService from './services/shopifyService';
import Navigation from './components/common/Navigation';
import ProductSearchPage from './pages/ProductSearchPage';
import UserManagementPage from './pages/UserManagementPage';
import OrderListPage from './pages/OrderListPage';
import OrderDetailPage from './pages/OrderDetailPage';
import PurchaseOrderListPage from './components/orders/PurchaseOrderListPage.js';
import PriceManagementPage from './pages/PriceManagementPage';
import './index.css';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <div>🔄 Yükleniyor...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Login Component
const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await login(email, password);
      window.location.href = '/';
    } catch (err) {
      setError('Geçersiz email veya şifre!');
      setLoading(false);
    }
  };

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '80vh'
    }}>
      <form
        onSubmit={handleSubmit}
        autoComplete="off"
        style={{
          padding: '2rem',
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          width: '100%',
          maxWidth: '400px'
        }}
      >
        <h2 style={{ textAlign: 'center', marginBottom: '1.5rem', color: '#1976d2' }}>🔐 Admin Girişi</h2>

        {error && (
          <div style={{
            color: 'red',
            backgroundColor: '#ffe6e6',
            padding: '0.75rem',
            borderRadius: '4px',
            marginBottom: '1rem',
            textAlign: 'center',
            fontSize: '0.9rem'
          }}>
            {error}
          </div>
        )}

        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Email:</label>
          <input
            type="text"
            name="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            placeholder="Email adresinizi giriniz"
            autoComplete="username"
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              boxSizing: 'border-box',
              fontSize: '1rem'
            }}
          />
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Şifre:</label>
          <input
            type="password"
            name="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            placeholder="Şifrenizi giriniz"
            autoComplete="current-password"
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              boxSizing: 'border-box',
              fontSize: '1rem'
            }}
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          style={{
            width: '100%',
            padding: '0.75rem',
            backgroundColor: loading ? '#ccc' : '#1976d2',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: loading ? 'not-allowed' : 'pointer',
            fontSize: '1rem',
            fontWeight: '500'
          }}
        >
          {loading ? 'Giriş Yapılıyor...' : 'Giriş Yap'}
        </button>

        <div style={{ marginTop: '1.5rem', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
          <p style={{ margin: 0, fontSize: '0.8rem', color: '#666', textAlign: 'center' }}>
            <strong>medidepot.com</strong><br />
            📧<br />
            🔑
          </p>
        </div>
      </form>
    </div>
  );
};

// Layout Component
const Layout = ({ children }) => {
  const { user, logout } = useAuth();

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f5f5f5',
      paddingBottom: '70px'
    }}>
      <header style={{
        backgroundColor: '#1976d2',
        color: 'white',
        padding: '1rem',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        position: 'sticky',
        top: 0,
        zIndex: 999
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '1.5rem' }}>🛍️ Shopify Admin Panel</h1>
            <p style={{ margin: 0, fontSize: '0.9rem', opacity: 0.8 }}>Professional Product Management</p>
          </div>

          {user && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <span style={{ fontSize: '0.9rem' }}>
                👋 {user.email} ({user.role})
              </span>
              <button
                onClick={logout}
                style={{
                  padding: '0.25rem 0.75rem',
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  border: '1px solid rgba(255,255,255,0.3)',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.8rem'
                }}
              >
                Çıkış
              </button>
            </div>
          )}
        </div>
      </header>

      <main style={{
        padding: '2rem',
        maxWidth: '1200px',
        margin: '0 auto',
        minHeight: 'calc(100vh - 140px)'
      }}>
        {children}
      </main>

      <Navigation />
    </div>
  );
};

// Dashboard Component
const Dashboard = () => {
  const [stats, setStats] = useState({
    shopifyProducts: 0,
    databaseProducts: 0,
    syncStatus: '',
    syncDifference: 0,
    loading: false,
    error: ''
  });

  const loadStats = useCallback(async (showLoading = false) => {
    try {
      if (showLoading) {
        setStats(prev => ({ ...prev, loading: true, error: '' }));
      }

      const syncAnalysis = await ShopifyService.getSyncStatusAnalysis();

      if (syncAnalysis.success && syncAnalysis.data) {
        setStats({
          shopifyProducts: syncAnalysis.data.shopifyProductCount || 0,
          databaseProducts: syncAnalysis.data.databaseProductCount || 0,
          syncStatus: syncAnalysis.data.syncStatus || 'UNKNOWN',
          syncDifference: syncAnalysis.data.syncDifference || 0,
          loading: false,
          error: ''
        });
      } else {
        throw new Error(syncAnalysis.message || 'Analytics data could not be loaded');
      }

    } catch (error) {
      console.error('❌ Dashboard error:', error);
      setStats(prev => ({
        ...prev,
        loading: false,
        error: `Error: ${error.message}`
      }));
    }
  }, []);

  const handleSync = async (type) => {
    try {
      setStats(prev => ({ ...prev, loading: true, error: '' }));

      let result;
      switch (type) {
        case 'incremental':
          result = await ShopifyService.triggerIncrementalSync();
          break;
        case 'full':
          result = await ShopifyService.triggerFullSync();
          break;
        case 'test':
          result = await ShopifyService.triggerTestSync();
          break;
        default:
          throw new Error('Geçersiz sync türü');
      }

      await loadStats(false);
      alert(`✅ ${type === 'incremental' ? 'Günlük' : type === 'full' ? 'Tam' : 'Test'} sync tamamlandı! ${result.message}`);

    } catch (error) {
      console.error('❌ Sync error:', error);
      setStats(prev => ({ ...prev, loading: false }));
      alert(`❌ Sync hatası: ${error.message}`);
    }
  };

  useEffect(() => {
    loadStats(true);
  }, [loadStats]);

  const getSyncStatusText = () => {
    switch (stats.syncStatus) {
      case 'UP_TO_DATE': return '✅ Güncel';
      case 'OUT_OF_SYNC': return '⚠️ Eksik Var';
      default: return '❓ Bilinmiyor';
    }
  };

  return (
    <div>
      <h2 style={{ color: '#1976d2', marginBottom: '1rem' }}>🎯 Dashboard</h2>

      {stats.error && (
        <div style={{
          padding: '1rem',
          backgroundColor: '#f8d7da',
          color: '#721c24',
          borderRadius: '4px',
          marginBottom: '1rem'
        }}>
          <strong>❌ Error:</strong> {stats.error}
        </div>
      )}

      {stats.loading && (
        <div style={{
          padding: '1rem',
          backgroundColor: '#fff3cd',
          color: '#856404',
          borderRadius: '4px',
          marginBottom: '1rem',
          textAlign: 'center'
        }}>
          <p style={{ margin: 0 }}>🔄 Loading data...</p>
        </div>
      )}

      {/* Analytics Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '1.5rem',
        marginBottom: '3rem'
      }}>
        {/* Shopify Card */}
        <div style={{
          backgroundColor: '#f8fafc',
          padding: '2rem',
          borderRadius: '16px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          border: '1px solid #e2e8f0',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: 'linear-gradient(90deg, #667eea, #764ba2)'
          }}></div>
          <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
            <div style={{
              backgroundColor: '#e3f2fd',
              padding: '0.75rem',
              borderRadius: '12px',
              marginRight: '1rem'
            }}>
              <span style={{ fontSize: '1.25rem', color: '#1976d2' }}>📦</span>
            </div>
            <div style={{ flex: 1 }}>
              <h3 style={{ margin: '0 0 0.25rem 0', fontSize: '0.9rem', color: '#64748b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>SHOPIFY</h3>
              <p style={{ margin: 0, fontSize: '0.8rem', color: '#94a3b8' }}>Toplam Ürün</p>
            </div>
          </div>
          <p style={{ fontSize: '2.5rem', fontWeight: '700', margin: '0 0 0.5rem 0', color: '#1e293b' }}>
            {stats.shopifyProducts}
          </p>
        </div>

        {/* Database Card */}
        <div style={{
          backgroundColor: '#f8fafc',
          padding: '2rem',
          borderRadius: '16px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          border: '1px solid #e2e8f0',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: 'linear-gradient(90deg, #11998e, #38ef7d)'
          }}></div>
          <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
            <div style={{
              backgroundColor: '#e8f5e8',
              padding: '0.75rem',
              borderRadius: '12px',
              marginRight: '1rem'
            }}>
              <span style={{ fontSize: '1.25rem', color: '#2e7d32' }}>💾</span>
            </div>
            <div style={{ flex: 1 }}>
              <h3 style={{ margin: '0 0 0.25rem 0', fontSize: '0.9rem', color: '#64748b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>DATABASE</h3>
              <p style={{ margin: 0, fontSize: '0.8rem', color: '#94a3b8' }}>Kayıtlı Ürün</p>
            </div>
          </div>
          <p style={{ fontSize: '2.5rem', fontWeight: '700', margin: '0 0 0.5rem 0', color: '#1e293b' }}>
            {stats.databaseProducts}
          </p>
        </div>

        {/* Status Card */}
        <div style={{
          backgroundColor: '#f8fafc',
          padding: '2rem',
          borderRadius: '16px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          border: '1px solid #e2e8f0',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: stats.syncStatus === 'UP_TO_DATE' ?
              'linear-gradient(90deg, #10b981, #34d399)' :
              'linear-gradient(90deg, #f59e0b, #fbbf24)'
          }}></div>
          <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
            <div style={{
              backgroundColor: stats.syncStatus === 'UP_TO_DATE' ? '#d1fae5' : '#fef3c7',
              padding: '0.75rem',
              borderRadius: '12px',
              marginRight: '1rem'
            }}>
              <span style={{
                fontSize: '1.25rem',
                color: stats.syncStatus === 'UP_TO_DATE' ? '#059669' : '#d97706'
              }}>🔄</span>
            </div>
            <div style={{ flex: 1 }}>
              <h3 style={{ margin: '0 0 0.25rem 0', fontSize: '0.9rem', color: '#64748b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>DURUM</h3>
              <p style={{ margin: 0, fontSize: '0.8rem', color: '#94a3b8' }}>Senkronizasyon</p>
            </div>
          </div>
          <p style={{
            fontSize: '1.5rem',
            fontWeight: '700',
            margin: '0 0 0.5rem 0',
            color: stats.syncStatus === 'UP_TO_DATE' ? '#059669' : '#d97706'
          }}>
            {getSyncStatusText()}
          </p>
        </div>

        {/* Difference Card */}
        <div style={{
          backgroundColor: '#f8fafc',
          padding: '2rem',
          borderRadius: '16px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          border: '1px solid #e2e8f0',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: stats.syncDifference === 0 ?
              'linear-gradient(90deg, #8b5cf6, #a78bfa)' :
              'linear-gradient(90deg, #f97316, #fb923c)'
          }}></div>
          <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
            <div style={{
              backgroundColor: stats.syncDifference === 0 ? '#ede9fe' : '#ffedd5',
              padding: '0.75rem',
              borderRadius: '12px',
              marginRight: '1rem'
            }}>
              <span style={{
                fontSize: '1.25rem',
                color: stats.syncDifference === 0 ? '#7c3aed' : '#ea580c'
              }}>📊</span>
            </div>
            <div style={{ flex: 1 }}>
              <h3 style={{ margin: '0 0 0.25rem 0', fontSize: '0.9rem', color: '#64748b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>FARK</h3>
              <p style={{ margin: 0, fontSize: '0.8rem', color: '#94a3b8' }}>Eksik Ürün</p>
            </div>
          </div>
          <p style={{
            fontSize: '2.5rem',
            fontWeight: '700',
            margin: '0 0 0.5rem 0',
            color: stats.syncDifference === 0 ? '#7c3aed' : '#ea580c'
          }}>
            {stats.syncDifference > 0 ? '+' : ''}{stats.syncDifference}
          </p>
        </div>
      </div>

      {/* Sync Buttons */}
      <div style={{
        backgroundColor: 'white',
        padding: '2rem',
        borderRadius: '12px',
        boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
        border: '1px solid #e2e8f0',
        marginBottom: '2rem'
      }}>
        <h3 style={{
          color: '#1e293b',
          marginBottom: '1.5rem',
          fontSize: '1.3rem',
          fontWeight: '600',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <span>🚀</span>
          Senkronizasyon İşlemleri
        </h3>

        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
          <button
            onClick={() => handleSync('incremental')}
            disabled={stats.loading}
            style={{
              padding: '0.875rem 1.5rem',
              backgroundColor: stats.loading ? '#cbd5e1' : '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: stats.loading ? 'not-allowed' : 'pointer',
              fontSize: '0.95rem',
              fontWeight: '600',
              opacity: stats.loading ? 0.6 : 1,
              flex: '1',
              minWidth: '140px'
            }}
          >
            {stats.loading ? '⏳' : '🌅'} {stats.loading ? 'Çalışıyor...' : 'Günlük Sync'}
          </button>

          <button
            onClick={() => handleSync('full')}
            disabled={stats.loading}
            style={{
              padding: '0.875rem 1.5rem',
              backgroundColor: stats.loading ? '#cbd5e1' : '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: stats.loading ? 'not-allowed' : 'pointer',
              fontSize: '0.95rem',
              fontWeight: '600',
              opacity: stats.loading ? 0.6 : 1,
              flex: '1',
              minWidth: '140px'
            }}
          >
            {stats.loading ? '⏳' : '🚀'} {stats.loading ? 'Çalışıyor...' : 'Tam Sync'}
          </button>

          <button
            onClick={() => handleSync('test')}
            disabled={stats.loading}
            style={{
              padding: '0.875rem 1.5rem',
              backgroundColor: stats.loading ? '#cbd5e1' : '#6b7280',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: stats.loading ? 'not-allowed' : 'pointer',
              fontSize: '0.95rem',
              fontWeight: '600',
              opacity: stats.loading ? 0.6 : 1,
              flex: '1',
              minWidth: '140px'
            }}
          >
            {stats.loading ? '⏳' : '🧪'} {stats.loading ? 'Çalışıyor...' : 'Test Sync'}
          </button>

          <button
            onClick={() => loadStats(true)}
            style={{
              padding: '0.875rem 1.5rem',
              backgroundColor: '#f1f5f9',
              color: '#475569',
              border: '1px solid #cbd5e1',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '0.95rem',
              fontWeight: '600',
              flex: '1',
              minWidth: '140px'
            }}
          >
            🔄 Verileri Yenile
          </button>
        </div>
      </div>
    </div>
  );
};

// SyncPage Component
const SyncPage = () => {
  const [syncStatus, setSyncStatus] = useState({
    loading: false,
    result: null,
    error: ''
  });

  const handleSync = async (type) => {
    try {
      setSyncStatus({ loading: true, result: null, error: '' });

      let result;
      switch (type) {
        case 'full':
          result = await ShopifyService.triggerFullSync();
          break;
        case 'incremental':
          result = await ShopifyService.triggerIncrementalSync();
          break;
        case 'test':
          result = await ShopifyService.triggerTestSync();
          break;
        default:
          return;
      }

      setSyncStatus({
        loading: false,
        result,
        error: ''
      });

    } catch (error) {
      console.error('❌ Sync error:', error);
      setSyncStatus({
        loading: false,
        result: null,
        error: error.message
      });
    }
  };

  return (
    <div>
      <h2 style={{ color: '#1976d2', marginBottom: '1rem' }}>🔄 Sync Operations</h2>

      {syncStatus.error && (
        <div style={{
          padding: '1rem',
          backgroundColor: '#f8d7da',
          color: '#721c24',
          borderRadius: '8px',
          marginBottom: '1rem'
        }}>
          <strong>❌ Error:</strong> {syncStatus.error}
        </div>
      )}

      {syncStatus.result && (
        <div style={{
          padding: '1rem',
          backgroundColor: '#d1edff',
          color: '#055c9d',
          borderRadius: '8px',
          marginBottom: '1rem'
        }}>
          <strong>✅ Success:</strong> {syncStatus.result.message}
        </div>
      )}

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: '1rem',
        marginBottom: '2rem'
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <h3 style={{ color: '#1976d2', margin: '0 0 1rem 0' }}>🚀 Full Sync</h3>
          <p style={{ color: '#666', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
            Tüm ürünleri Shopify'dan database'e senkronize eder.
          </p>
          <button
            onClick={() => handleSync('full')}
            disabled={syncStatus.loading}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#1976d2',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: syncStatus.loading ? 'not-allowed' : 'pointer',
              opacity: syncStatus.loading ? 0.6 : 1,
              width: '100%'
            }}
          >
            {syncStatus.loading ? '⏳ Running...' : '🔄 Start Full Sync'}
          </button>
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <h3 style={{ color: '#2e7d32', margin: '0 0 1rem 0' }}>🌅 Daily Sync</h3>
          <p style={{ color: '#666', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
            Sadece yeni ve değişen ürünleri senkronize eder.
          </p>
          <button
            onClick={() => handleSync('incremental')}
            disabled={syncStatus.loading}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#2e7d32',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: syncStatus.loading ? 'not-allowed' : 'pointer',
              opacity: syncStatus.loading ? 0.6 : 1,
              width: '100%'
            }}
          >
            {syncStatus.loading ? '⏳ Running...' : '🔄 Start Daily Sync'}
          </button>
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <h3 style={{ color: '#ed6c02', margin: '0 0 1rem 0' }}>🧪 Test Sync</h3>
          <p style={{ color: '#666', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
            İlk 50 ürünü test amaçlı senkronize eder.
          </p>
          <button
            onClick={() => handleSync('test')}
            disabled={syncStatus.loading}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#ed6c02',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: syncStatus.loading ? 'not-allowed' : 'pointer',
              opacity: syncStatus.loading ? 0.6 : 1,
              width: '100%'
            }}
          >
            {syncStatus.loading ? '⏳ Running...' : '🔄 Start Test Sync'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Main App Component
function App() {
  return (
    <AuthProvider>
      <Router>
        <Layout>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/sync"
              element={
                <ProtectedRoute>
                  <SyncPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/products"
              element={
                <ProtectedRoute>
                  <ProductSearchPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/users"
              element={
                <ProtectedRoute>
                  <UserManagementPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/orders"
              element={
                <ProtectedRoute>
                  <OrderListPage />
                </ProtectedRoute>
              }
            />
            <Route path="/price-management" element={
              <ProtectedRoute>
                <PriceManagementPage />
              </ProtectedRoute>} />

            <Route
              path="/orders/:id"
              element={
                <ProtectedRoute>
                  <OrderDetailPage />
                </ProtectedRoute>
              }
            />
            {/* 🔥 YENİ: PURCHASE ORDERS ROUTE */}
            <Route
              path="/purchase-orders"
              element={
                <ProtectedRoute>
                  <PurchaseOrderListPage />
                </ProtectedRoute>
              }
            />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Layout>
      </Router>
    </AuthProvider>
  );
}

export default App;